import { fetchWrapper, IBaseResponse } from '../../apiBase';
import { IRepositoryFileRequest, IRepository, IResourceVersion } from '@/types';
import { BASE_API_URL } from '@/config/environment';
import type { TDfQuery, IDfQueryResponse, IQueryQuotaUsage, TQueryRequest } from '@/lib/dataframeQueryApi';

const DEFAULT_PAGE_SIZE = 100;

interface IDfQueriesResponse extends IBaseResponse {
  queries: TDfQuery[];
}

type TFetchUserQueryParams = {
  repository: IRepository;
  workspaceId: string;
  resource?: IResourceVersion;
  page_size?: number | string;
  page?: number | string;
  token?: string;
};

const targetUrlBase = (repository: IRepository, workspaceId: string) => {
  return `${BASE_API_URL}/repos/${repository.namespace}/${repository.name}/workspaces/${workspaceId}/data_frames/data_frame_queries`;
};

export const fetchUserQueries = async ({
  repository,
  workspaceId,
  resource,
  token,
  page_size = 1000,
  page = 1,
}: TFetchUserQueryParams): Promise<IDfQueriesResponse> => {
  const { namespace, name: repositoryName } = repository;
  const targetUrl = `${targetUrlBase(repository, workspaceId)}/file/${resource?.path}?page=${page}&page_size=${page_size}`;
  // console.log('fetchUserQueries > targetUrl', targetUrl);

  return fetchWrapper({
    resourceName: 'fetchWorkspaceUserQueries',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, workspaceId, repositoryName },
  });
};

type TFetchQueryParams = {
  repository: IRepository;
  workspaceId: string;
  resource: IResourceVersion;
  request?: TQueryRequest;
  refetch?: boolean;
  queryId?: string;
  sql?: string;
  nlp?: string;
  parentId?: string;
  token?: string;
};

export const fetchQueryById = async ({
  repository,
  workspaceId,
  queryId,
  token,
}: {
  repository: IRepository;
  workspaceId: string;
  queryId: string;
  token?: string;
}): Promise<IDfQueryResponse> => {
  const { namespace, name: repositoryName } = repository;
  const targetUrl = `${targetUrlBase(repository, workspaceId)}/${queryId}`;
  // console.log('fetchQueryById > targetUrl', targetUrl);

  return fetchWrapper({
    resourceName: 'workspaceDataframeQuery',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, workspaceId, repositoryName },
  });
};

const fetchQueryBySql = async ({
  repository,
  workspaceId,
  sql,
  parentId,
  resource,
  refetch = false,
  token,
}: TFetchQueryParams): Promise<IDfQueryResponse> => {
  const { namespace, name: repositoryName } = repository;
  // const path = [resource.version, resource.path].join('/');
  const path = resource.path;
  const targetUrl = `${targetUrlBase(repository, workspaceId)}/get_or_create/file/${path}`;

  const payload = {
    sql,
    refetch_query: refetch,
    parent_id: parentId,
  };
  // console.log('fetchQueryBySql > targetUrl', targetUrl, payload);

  return fetchWrapper({
    resourceName: 'workspaceDataframeQuery',
    method: 'PUT',
    contentType: 'application/json',
    body: JSON.stringify(payload),
    targetUrl,
    authToken: token,
    resourceContext: { namespace, workspaceId, repositoryName },
  });
};

const fetchQueryByNlp = async ({
  repository,
  workspaceId,
  nlp,
  resource,
  refetch = false,
  token,
}: TFetchQueryParams): Promise<IDfQueryResponse> => {
  const { namespace, name: repositoryName } = repository;
  // const path = [resource.version, resource.path].join('/');
  const path = resource.path;
  console.log('fetchQueryByNlp > path', path);
  const targetUrl = `${targetUrlBase(repository, workspaceId)}/get_or_create/file/${path}`;
  const payload = { nlp, refetch_query: refetch };
  // console.log('fetchQueryByNlp > targetUrl', targetUrl, payload);

  return fetchWrapper({
    resourceName: 'workspaceDataframeQuery',
    method: 'PUT',
    contentType: 'application/json',
    body: JSON.stringify(payload),
    targetUrl,
    authToken: token,
    resourceContext: { namespace, workspaceId, repositoryName },
  });
};

export const fetchQuery = async ({
  repository,
  workspaceId,
  resource,
  request,
  refetch = false,
  token,
}: TFetchQueryParams): Promise<IDfQueryResponse> => {
  // const { namespace, name: repositoryName } = repository;
  // const path = [resource.version, resource.path].join('/');
  // const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/data_frame_queries/get_or_create/file/${path}`;
  // console.log('fetchQuery > targetUrl', targetUrl, { request });

  if (!request) {
    console.info('fetchQuery > request is required');
    return {} as IDfQueryResponse;
  }

  if (request.type === 'id' && request.value) {
    return fetchQueryById({ repository, workspaceId, queryId: request.value, token });
  }

  if (request.type === 'sql') {
    return fetchQueryBySql({
      repository,
      workspaceId,
      parentId: request.parentId,
      sql: request.value,
      resource,
      refetch,
      token,
    });
  }

  if (request.type === 'nlp') {
    return fetchQueryByNlp({ repository, workspaceId, nlp: request.value, resource, refetch: request.refetch, token });
  }

  console.error('fetchQuery > request.type is not supported, or id is missing', { request });
  return {} as IDfQueryResponse;
};

export type { TDfQuery, IDfQueryResponse, IQueryQuotaUsage, TQueryRequest };
